// Centralized API client with auto-refresh token handling
import { useAuthStore } from '../store/auth.store';

const API_URL = import.meta.env.VITE_API_URL || 'https://paathshaala-saas-worker.rame6.workers.dev';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string; details?: unknown };
  meta?: { page: number; pageSize: number; total: number; totalPages: number };
}

export class ApiError extends Error {
  code: string;
  status: number;
  details?: unknown;

  constructor(code: string, message: string, status: number, details?: unknown) {
    super(message);
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

async function refreshToken(): Promise<string | null> {
  const { token } = useAuthStore.getState();
  if (!token) return null;

  try {
    const response = await fetch(`${API_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) return null;

    const data = await response.json();
    if (data.success) {
      return token;
    }
    return null;
  } catch {
    return null;
  }
}

async function getValidToken(): Promise<string | null> {
  const { token } = useAuthStore.getState();
  if (!token) return null;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = JSON.parse(atob(parts[1]));
    const exp = payload.exp * 1000;
    const now = Date.now();

    if (exp - now < 60 * 60 * 1000) {
      if (isRefreshing && refreshPromise) {
        return refreshPromise;
      }

      isRefreshing = true;
      refreshPromise = refreshToken();
      const newToken = await refreshPromise;
      isRefreshing = false;
      refreshPromise = null;

      if (newToken) {
        useAuthStore.setState({ token: newToken });
      } else {
        useAuthStore.getState().logout();
        return null;
      }
      return newToken;
    }

    return token;
  } catch {
    return token;
  }
}

async function apiRequest<T>(
  url: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = await getValidToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${url}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    useAuthStore.getState().logout();
    throw new ApiError('UNAUTHORIZED', 'Session expired. Please login again.', 401);
  }

  const data = await response.json() as ApiResponse<T>;

  if (!data.success) {
    throw new ApiError(
      data.error?.code || 'UNKNOWN',
      data.error?.message || 'An error occurred',
      response.status,
      data.error?.details
    );
  }

  return data;
}

export const api = {
  get: <T>(url: string) => apiRequest<T>(url, { method: 'GET' }),
  post: <T>(url: string, body: unknown) => apiRequest<T>(url, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(url: string, body: unknown) => apiRequest<T>(url, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(url: string, body: unknown) => apiRequest<T>(url, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(url: string) => apiRequest<T>(url, { method: 'DELETE' }),
  upload: async (url: string, formData: FormData): Promise<ApiResponse<unknown>> => {
    const token = await getValidToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_URL}${url}`, {
      method: 'POST',
      body: formData,
      headers,
    });

    if (response.status === 401) {
      useAuthStore.getState().logout();
      throw new ApiError('UNAUTHORIZED', 'Session expired. Please login again.', 401);
    }

    return response.json();
  },
};

export { API_URL };
